/* istanbul ignore file */
/**
 * Created by rathompson on 6/22/22
 * File: DomainModal
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { VFC, useCallback, useEffect, useMemo, useState, CSSProperties } from 'react'
import {
  Modal,
  ModalHeader,
  ModalProps,
} from '@panwds/react-ui'
import {
  STEP_DOMAIN_INFO,
  STEP_VERIFY_INSTRUCTIONS,
  TITLE_ADD_IDENTITY_FEDERATIONS,
} from '../../../constants/LabelConstants'
import Sidebar from '../../common/modal/sidebar/Sidebar'
import VerificationInstructions from '../forms/VerificationInstructions'
import DomainInfoForm from '../forms/DomainInfoForm'

export const SIDEBAR_OPTIONS = {
  DOMAIN_INFO: {
    name: STEP_DOMAIN_INFO,
  },
  VERIFY: {
    name: STEP_VERIFY_INSTRUCTIONS,
  }
}

const DomainModal: VFC<ModalProps & {
  closeModal: () => void,
  step: { name: string },
  onAddOwnerHandler: Function,
  userFederation: any,
  fetchData: Function,
  email: string,
  verificationString: string,
  shouldFetch?: boolean,
  federations: any,
  savedDomain?: string,
}> = ({
  isOpen,
  closeModal,
  step = SIDEBAR_OPTIONS.DOMAIN_INFO,
  onAddOwnerHandler,
  userFederation,
  fetchData,
  email,
  verificationString: _verificationString,
  shouldFetch,
  federations,
  savedDomain,
}) => {

  const [loading, setLoading] = useState(false)
  const [activeStep, setActiveStep] = useState<{ name: string, icon?: boolean } | null>(null)
  const [domain, setDomain] = useState(savedDomain ?? '')
  const [verificationString, setVerificationString] = useState(_verificationString)

  useEffect(() => {
    setActiveStep(step)
  }, [step])

  useEffect(() => {
    setVerificationString(_verificationString)
  }, [_verificationString])

  const onBackPressed = useCallback(() => {
    switch (activeStep) {
      case SIDEBAR_OPTIONS.VERIFY:
        setActiveStep(SIDEBAR_OPTIONS.DOMAIN_INFO)
        break
      case SIDEBAR_OPTIONS.DOMAIN_INFO:
      default:
        closeModal()
    }
  }, [activeStep, closeModal])

  const onNextPressed = useCallback(() => {
    switch (activeStep) {
      case SIDEBAR_OPTIONS.DOMAIN_INFO:
        setActiveStep(SIDEBAR_OPTIONS.VERIFY)
        break
      case SIDEBAR_OPTIONS.VERIFY:
      default:
        closeModal()
    }
  }, [activeStep, closeModal])

  const activeStepContent = useMemo(() => {
    switch (activeStep) {
      case SIDEBAR_OPTIONS.VERIFY:
        return <VerificationInstructions {...{ loading, setLoading, onBackPressed, onNextPressed, onAddOwnerHandler, domain, fetchData, userFederation, verificationString, shouldFetch }} />
      case SIDEBAR_OPTIONS.DOMAIN_INFO:
        return <DomainInfoForm {...{ loading, setLoading, onBackPressed, onNextPressed, setDomain, domain, verificationString, setVerificationString, email, federations, savedDomain }} />
      default:
        return ''
    }
  }, [activeStep, email, loading, onBackPressed, onNextPressed, onAddOwnerHandler, domain, fetchData, userFederation, verificationString, shouldFetch, federations, savedDomain])

  const sidebarOptions = useMemo(() => {
    const copy: { [key: string]: { name: string, icon?: boolean } } = Object.assign({}, SIDEBAR_OPTIONS)
    copy.DOMAIN_INFO.icon = false
    if (activeStep?.name === SIDEBAR_OPTIONS.VERIFY.name && domain?.length) {
      copy.DOMAIN_INFO.icon = true
    }
    return Object.values(copy)
  }, [activeStep, domain])

  return (
    <Modal
      size='lg'
      style={{ top: '50%', '--tw-translate-y': '-50%', minHeight: '600px', minWidth: '800px' } as CSSProperties}
      onClose={closeModal}
      {...{ isOpen }}
    >
      <ModalHeader title={TITLE_ADD_IDENTITY_FEDERATIONS} />
      <div className="tw-flex tw-flex-1 tw-relative tw-max-h-full">
        <Sidebar options={sidebarOptions} currentOption={activeStep}/>
        <div className="tw-absolute tw-right-0 tw-bottom-0 tw-top-0" style={{ left: '200px' }}>
          {activeStepContent}
        </div>
      </div>
    </Modal>
  )
}

DomainModal.propTypes = {}
export default DomainModal
